import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaCheck, FaSave, FaFileAlt, FaCheckCircle, FaChartBar, FaFilePdf } from 'react-icons/fa';
import './Assessment.css';

// Hardcoded standards mapping for common question types
const getStandardsForQuestion = (question) => {
  const questionLower = question.toLowerCase();
  
  // Academic continuity standards
  if (questionLower.includes('course') || questionLower.includes('class') || questionLower.includes('academic')) {
    return 'ISO 22301, NFPA 1600, NIST 800-34, EDUCAUSE HEISC';
  }
  
  // IT/Technology standards
  if (questionLower.includes('technology') || questionLower.includes('it') || questionLower.includes('system') || questionLower.includes('cyber')) {
    return 'NIST 800-34, NIST CSF, ISO 27001, ISO 22301';
  }
  
  // Emergency management standards
  if (questionLower.includes('emergency') || questionLower.includes('crisis') || questionLower.includes('incident')) {
    return 'NFPA 1600, ISO 22301, EMAP';
  }
  
  // Business continuity standards
  if (questionLower.includes('business') || questionLower.includes('continuity') || questionLower.includes('recovery')) {
    return 'ISO 22301, NFPA 1600, NIST 800-34';
  }
  
  // Supply chain standards
  if (questionLower.includes('supply') || questionLower.includes('vendor') || questionLower.includes('third party')) {
    return 'ISO 22301, ISO 28000, NIST 800-161';
  }
  
  // Default standards for other questions
  return 'ISO 22301, NFPA 1600';
};

// Function to generate specific help text based on the question content
const generateHelpText = (question) => {
  // Extract key terms from the question
  const questionLower = question.toLowerCase();
  
  // Check for specific question types and provide tailored guidance
  if (questionLower.includes('charter') || questionLower.includes('policy')) {
    return 'This question assesses whether your organization has formalized its resilience commitments through official documentation. Consider whether these documents are approved by leadership and regularly reviewed.';
  } else if (questionLower.includes('roadmap') || questionLower.includes('plan')) {
    return 'This question evaluates your organization\'s strategic planning for resilience initiatives. Consider whether you have documented plans with clear timelines, resource allocations, and accountability measures.';
  } else if (questionLower.includes('fund') || questionLower.includes('budget')) {
    return 'This question examines how resilience activities are financially supported in your organization. Consider whether dedicated funding exists and how it\'s allocated across different resilience functions.';
  } else if (questionLower.includes('train') || questionLower.includes('awareness')) {
    return 'This question assesses your organization\'s approach to building resilience knowledge and skills. Consider the frequency, quality, and reach of your training programs.';
  } else if (questionLower.includes('test') || questionLower.includes('exercise') || questionLower.includes('drill')) {
    return 'This question evaluates how your organization validates its resilience capabilities. Consider the scope, frequency, and outcomes of your testing activities.';
  } else if (questionLower.includes('report') || questionLower.includes('metric') || questionLower.includes('measure')) {
    return 'This question examines how your organization tracks and communicates resilience performance. Consider what metrics you collect and how they inform decision-making.';
  } else if (questionLower.includes('governance') || questionLower.includes('oversight')) {
    return 'This question assesses your organization\'s management structure for resilience activities. Consider roles, responsibilities, and reporting lines for resilience functions.';
  } else if (questionLower.includes('incident') || questionLower.includes('response')) {
    return 'This question evaluates your organization\'s ability to handle disruptive events. Consider your incident management procedures, roles, and communication protocols.';
  } else if (questionLower.includes('recovery') || questionLower.includes('continuity')) {
    return 'This question examines your organization\'s ability to maintain or restore critical functions during disruptions. Consider your recovery strategies, timeframes, and resource requirements.';
  } else if (questionLower.includes('risk') || questionLower.includes('threat')) {
    return 'This question assesses how your organization identifies and manages potential disruptions. Consider your risk assessment methodologies and mitigation strategies.';
  } else if (questionLower.includes('vendor') || questionLower.includes('third party') || questionLower.includes('supplier')) {
    return 'This question evaluates how your organization manages resilience risks in your supply chain. Consider your vendor assessment, contractual requirements, and monitoring practices.';
  } else if (questionLower.includes('compliance') || questionLower.includes('regulation') || questionLower.includes('standard')) {
    return 'This question examines your organization\'s adherence to relevant resilience requirements. Consider the frameworks, regulations, and standards applicable to your industry.';
  }
  
  // Default case for questions that don't match specific patterns
  return 'This question evaluates an important aspect of your organization\'s resilience program. Consider your current processes, documentation, and implementation effectiveness.';
};

const AssessmentDetail = ({ editMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isUpdateMode = editMode || new URLSearchParams(location.search).get('mode') === 'update';

  // State for assessment data
  const [assessment, setAssessment] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [savingQuestions, setSavingQuestions] = useState({});
  const [categoryQuestions, setCategoryQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState({});
  const [user, setUser] = useState(null);
  const [showReportOptions, setShowReportOptions] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [lastSaved, setLastSaved] = useState('');
  
  // State for tracking Not Applicable questions and categories
  const [notApplicableCategories, setNotApplicableCategories] = useState([]);

  // Fetch assessment data
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/assessments/${id}`);
        const data = res.data;
        
        // Set state with assessment data
        setAssessment(data);
        setResponses(data.responses || []);
        setLoading(false);
        
        // Load Not Applicable categories if they exist
        if (data.notApplicableCategories && Array.isArray(data.notApplicableCategories)) {
          setNotApplicableCategories(data.notApplicableCategories);
          console.log('Loaded Not Applicable categories:', data.notApplicableCategories);
        }
        
        // Set saved answers state
        const savedAnswersState = {};
        data.responses.forEach(response => {
          savedAnswersState[response.questionId] = true;
        });
        setSavedAnswers(savedAnswersState);
        
        // Fetch maturity levels for each question if not already present
        if (data.industryType) {
          try {
            // Fetch assessment template with maturity levels
            const templateRes = await axios.get(`/api/assessments/template/${data.industryType}`);
            if (templateRes.data && templateRes.data.questions) {
              console.log('Template questions:', templateRes.data.questions);
              
              // Update responses with maturity levels and standards
              const updatedResponses = res.data.responses.map(response => {
                console.log('Processing response:', response.question);
                // Try to find matching question by exact question text or ID
                let matchingQuestion = templateRes.data.questions.find(q => 
                  q.question === response.question || 
                  (q.questionId && q.questionId === response.questionId) ||
                  (response.questionId && q._id && q._id.toString() === response.questionId)
                );
                
                // If no match found, try a more flexible approach with lowercase comparison
                if (!matchingQuestion) {
                  matchingQuestion = templateRes.data.questions.find(q => 
                    q.question.toLowerCase().trim() === response.question.toLowerCase().trim()
                  );
                }
                
                if (matchingQuestion) {
                  console.log('Found matching question:', matchingQuestion.question);
                  
                  // Log all properties of the matching question to see what's available
                  console.log('All question properties:', JSON.stringify(matchingQuestion));
                  
                  // Check various possible field names for standards
                  const standards = matchingQuestion.standards || 
                                   matchingQuestion.applicableStandards || 
                                   matchingQuestion['Applicable Standards'] ||
                                   matchingQuestion['Applicable Standards/Best Practices'] || '';
                  
                  console.log('Standards found for question:', matchingQuestion.question);
                  console.log('Standards value:', standards);
                  
                  // Create a new response object with the standards
                  const updatedResponse = {
                    ...response,
                    maturityLevels: matchingQuestion.maturityLevels || [],
                    standards: standards
                  };
                  
                  console.log('Updated response with standards:', updatedResponse);
                  return updatedResponse;
                }
                return response;
              });
              
              console.log('All updated responses:', updatedResponses);
              console.log('First response after update:', updatedResponses[0]);
              
              // Check if any responses have standards
              const responsesWithStandards = updatedResponses.filter(r => r.standards && r.standards.trim() !== '');
              console.log('Responses with standards:', responsesWithStandards.length);
              if (responsesWithStandards.length > 0) {
                console.log('Sample response with standards:', responsesWithStandards[0]);
              }
              
              setResponses(updatedResponses);
            }
          } catch (err) {
            console.error('Error fetching template:', err);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching assessment:', err);
        setError('Failed to load assessment. Please try again.');
        setLoading(false);
      }
    };

    // Fetch user data
    const fetchUser = async () => {
      try {
        const res = await axios.get('/api/auth/user');
        setUser(res.data);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };

    fetchAssessment();
    fetchUser();
  }, [id]);

  // Extract unique categories and set active category
  useEffect(() => {
    if (responses.length > 0) {
      // Extract unique categories
      const uniqueCategories = [...new Set(responses.map(r => r.category))];
      
      // For Financial assessment, use a specific order
      if (assessment && assessment.industryType === 'Financial') {
        const orderedCategories = [
          'Leadership',
          'Governance',
          'Assessment',
          'Strategy',
          'Implementation',
          'Validation',
          'Communication',
          'Risk Management',
          'Business Continuity',
          'Vendor Management'
        ];
        
        // Filter ordered categories to only include those that exist in responses
        const filteredOrderedCategories = orderedCategories.filter(
          category => uniqueCategories.includes(category)
        );
        
        // Add any categories that aren't in the ordered list
        const remainingCategories = uniqueCategories.filter(
          category => !orderedCategories.includes(category)
        );
        
        setCategories([...filteredOrderedCategories, ...remainingCategories]);
      } else {
        // For other industry types, sort alphabetically
        setCategories(uniqueCategories.sort());
      }
    }
  }, [responses, assessment]);

  // Set active category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      // For Financial assessment, set Leadership as default active category
      if (assessment && assessment.industryType === 'Financial' && categories.includes('Leadership')) {
        setActiveCategory('Leadership');
      } else {
        // Otherwise, set first category as active
        setActiveCategory(categories[0]);
      }
    }
  }, [categories, activeCategory, assessment]);

  // Update category questions whenever active category changes
  useEffect(() => {
    if (activeCategory) {
      const filteredQuestions = responses.filter(r => r.category === activeCategory);
      setCategoryQuestions(filteredQuestions);
    }
  }, [activeCategory, responses]);
  
  // Handle answer change
  const handleAnswerChange = (index, value) => {
    const updatedResponses = [...responses];
    updatedResponses[index].answer = value;
    setResponses(updatedResponses);
    
    // Mark this question as being saved
    const questionId = updatedResponses[index].questionId;
    setSavingQuestions(prev => ({
      ...prev,
      [questionId]: true
    }));
    
    // Clear saved indicator for this question
    if (savedAnswers[questionId]) {
      const updatedSavedAnswers = { ...savedAnswers };
      delete updatedSavedAnswers[questionId];
      setSavedAnswers(updatedSavedAnswers);
    }
    
    // Auto-save after a short delay for all modes (not just update mode)
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      console.log(`Auto-saving answer for question ID ${questionId}: ${value}`);
      saveAssessment();
    }, 1500);
    
    setAutoSaveTimeout(timeout);
  };
  
  // Handle notes change
  const handleNotesChange = (index, value) => {
    const updatedResponses = [...responses];
    updatedResponses[index].notes = value;
    setResponses(updatedResponses);
    
    // Mark this question as being saved
    const questionId = updatedResponses[index].questionId;
    setSavingQuestions(prev => ({
      ...prev,
      [questionId]: true
    }));
    
    // Clear saved indicator for this question
    if (savedAnswers[questionId]) {
      const updatedSavedAnswers = { ...savedAnswers };
      delete updatedSavedAnswers[questionId];
      setSavedAnswers(updatedSavedAnswers);
    }
    
    // Auto-save after a short delay for all modes (not just update mode)
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      console.log(`Auto-saving notes for question ID ${questionId}`);
      saveAssessment();
    }, 1500);
    
    setAutoSaveTimeout(timeout);
  };

  // Handle toggling a category as Not Applicable
  const handleToggleNotApplicableCategory = (category) => {
    // Create a copy of the current notApplicableCategories array
    const updatedNotApplicableCategories = [...notApplicableCategories];
    
    // Check if the category is already marked as Not Applicable
    const categoryIndex = updatedNotApplicableCategories.indexOf(category);
    
    if (categoryIndex === -1) {
      // If not in the array, add it
      updatedNotApplicableCategories.push(category);
      console.log(`Marked category as Not Applicable: ${category}`);
    } else {
      // If already in the array, remove it
      updatedNotApplicableCategories.splice(categoryIndex, 1);
      console.log(`Removed Not Applicable status from category: ${category}`);
    }
    
    // Update state
    setNotApplicableCategories(updatedNotApplicableCategories);
    
    // Auto-save after a short delay for all modes (not just update mode)
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      console.log(`Auto-saving after toggling category ${category} as Not Applicable`);
      saveAssessment();
    }, 1500);
    
    setAutoSaveTimeout(timeout);
  };
  
  // Handle toggling a question as Not Applicable
  const handleToggleNotApplicableQuestion = (index) => {
    const updatedResponses = [...responses];
    
    // Toggle the notApplicable property
    updatedResponses[index].notApplicable = !updatedResponses[index].notApplicable;
    
    // If marking as Not Applicable, clear the answer
    if (updatedResponses[index].notApplicable) {
      updatedResponses[index].answer = '';
      console.log(`Marked question as Not Applicable: ${updatedResponses[index].question}`);
    } else {
      console.log(`Removed Not Applicable status from question: ${updatedResponses[index].question}`);
    }
    
    setResponses(updatedResponses);
    
    // Mark this question as being saved
    const questionId = updatedResponses[index].questionId;
    setSavingQuestions(prev => ({
      ...prev,
      [questionId]: true
    }));
    
    // Clear saved indicator for this question
    if (savedAnswers[questionId]) {
      const updatedSavedAnswers = { ...savedAnswers };
      delete updatedSavedAnswers[questionId];
      setSavedAnswers(updatedSavedAnswers);
    }
    
    // Auto-save after a short delay for all modes (not just update mode)
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      console.log(`Auto-saving after toggling question ${questionId} as Not Applicable`);
      saveAssessment();
    }, 1500);
    
    setAutoSaveTimeout(timeout);
  };

  // Save assessment
  const saveAssessment = async (status = 'in-progress') => {
    try {
      setSaving(true);
      // Update the last saved timestamp with the current time
      const currentTime = new Date().toLocaleTimeString();
      setLastSaved(currentTime);
      console.log(`Auto-save triggered at ${currentTime}`);
      
      // Calculate progress percentage
      const answeredQuestions = responses.filter(r => r.answer && r.answer !== '').length;
      const progressPercentage = Math.round((answeredQuestions / responses.length) * 100);
      
      // Log responses before optimization for debugging
      console.log(`Saving assessment with ${responses.length} responses, ${answeredQuestions} answered`);
      
      // Ensure all responses have required fields
      const validResponses = responses.filter(r => r.questionId && r.question);
      if (validResponses.length !== responses.length) {
        console.warn(`Found ${responses.length - validResponses.length} invalid responses without required fields`);
      }
      
      // Optimize responses by removing unnecessary fields to reduce payload size
      // but ensure all required fields are present
      const optimizedResponses = validResponses.map(response => ({
        questionId: response.questionId,
        question: response.question,
        answer: response.answer || '',
        notes: response.notes || '',
        category: response.category || 'Uncategorized',
        notApplicable: response.notApplicable || false
      }));
      
      // Prepare data for submission
      const assessmentData = {
        _id: assessment._id,
        title: assessment.title,
        description: assessment.description,
        organizationName: assessment.organizationName,
        industryType: assessment.industryType,
        userId: assessment.userId,
        responses: optimizedResponses,
        notApplicableCategories,
        // In update mode, maintain 'completed' status even when saving progress
        status: isUpdateMode && status === 'in-progress' ? 'completed' : status,
        progress: progressPercentage
      };
      
      console.log(`Saving assessment ${id} with ${optimizedResponses.length} responses`);
      console.log('Sample response being saved:', optimizedResponses.length > 0 ? optimizedResponses[0] : 'No responses');
      
      // If status is completed, calculate score
      if (status === 'completed') {
        const totalScore = responses.reduce((sum, response) => {
          if (response.answer && response.answer !== 'N/A') {
            return sum + parseInt(response.answer);
          }
          return sum;
        }, 0);
        
        const maxPossibleScore = responses.filter(r => r.answer !== 'N/A').length * 5;
        const scorePercentage = Math.round((totalScore / maxPossibleScore) * 100);
        
        assessmentData.score = scorePercentage;
      }
      
      // Save to server
      console.log(`Sending PUT request to /api/assessments/${id}`);
      const res = await axios.put(`/api/assessments/${id}`, assessmentData);
      console.log('Server response:', res.status);
      
      // Update saved answers state for visual indicators
      const savedAnswersState = {};
      optimizedResponses.forEach(response => {
        if (response.questionId) {
          savedAnswersState[response.questionId] = true;
        }
      });
      setSavedAnswers(savedAnswersState);
      
      // Clear saving indicators
      setSavingQuestions({});
      
      // Update state with returned data
      setAssessment(res.data);
      
      // Set appropriate success message
      if (isUpdateMode) {
        setSuccess('Assessment updated successfully');
      } else if (status === 'completed') {
        setSuccess('Assessment completed successfully!');
      } else {
        setSuccess('Progress saved!');
      }
      
      setSaving(false);
      
      // Handle navigation based on mode and status
      if (status === 'completed' && !isUpdateMode) {
        // For normal completion, wait briefly then navigate to assessments list
        setTimeout(() => {
          navigate('/assessments');
        }, 2000);
      } else {
        // For save or update, clear success message after delay
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      }
    } catch (err) {
      console.error('Error saving assessment:', err);
      setError('Failed to save assessment. Please try again.');
      setSaving(false);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    }
  };

  // Generate report or navigate to report page
  const generateReport = async (reportType) => {
    try {
      setLoading(true);
      setError(null);
      
      // For Scorecard, navigate directly to the scorecard page
      if (reportType === 'Scorecard') {
        console.log(`Navigating to scorecard page for assessment: ${id}`);
        navigate(`/maturity-scorecard/${id}`);
        setLoading(false);
        return;
      }
      
      // For other report types, use the API
      // Map the report type to what the API expects
      let type;
      
      // For ScorecardDetails, navigate directly to the client-side report page
      if (reportType === 'ScorecardDetails') {
        console.log(`Navigating to Scorecard Details page for assessment: ${id}`);
        navigate(`/assessments/${id}/scorecard-details`);
        setLoading(false);
        return;
      } else {
        // Default case for any other report types
        type = reportType;
      }
      
      console.log(`Generating report of type: ${type} for assessment: ${id}`);
      
      try {
        // For report types, use the general reports endpoint with full URL
        const res = await axios.post('http://localhost:5001/api/reports/generate', { 
          type: type,
          assessmentId: id
        }, {
          headers: {
            'x-auth-token': localStorage.getItem('token')
          }
        });
        
        console.log('Report generated successfully:', res.data);
        
        // Redirect to the appropriate report page based on type
        if (res.data && res.data._id) {
          navigate(`/reports/${res.data._id}`);
        } else {
          setError('Report was generated but no ID was returned');
        }
        
        setLoading(false);
      } catch (apiErr) {
        console.error('Error generating report via API:', apiErr);
        setError('Failed to generate report. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error in report generation process:', err);
      setError('Failed to generate report. Please try again.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading assessment...</p>
      </div>
    );
  }

  if (!assessment) {
    return <div className="alert alert-danger">Assessment not found</div>;
  }

  return (
    <div className="assessment-detail">
      <div className="page-header">
        <div>
          <button onClick={() => navigate('/assessments')} className="btn btn-light back-button">
            <FaArrowLeft /> Back to Assessments
          </button>
          <h1>{assessment.title}</h1>
          <p>Organization: {assessment.organizationName || 'Your Organization'} | Industry: {assessment.industryType}</p>
        </div>
        <div className="status-container">
          <div className="status-badges-container" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div className={`status-badge ${assessment.status}`}>
              {assessment.status === 'completed' ? (
                <><FaCheck /> Completed</>
              ) : (
                'In Progress'
              )}
            </div>
            {!isUpdateMode && assessment.status !== 'completed' && (
              <div className="save-status-badge" style={{ 
                backgroundColor: '#4C5454', 
                color: '#FFFAFA', 
                padding: '4px 8px', 
                borderRadius: '4px',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {saving ? (
                  <><FaSave style={{ marginRight: '5px' }} /> Auto-saving</>
                ) : success ? (
                  <><FaCheck style={{ marginRight: '5px', color: '#D2BA7B' }} /> {success}</>
                ) : (
                  <><FaCheck style={{ marginRight: '5px', color: '#D2BA7B' }} /> Last saved: {lastSaved || 'Not yet saved'}</>
                )}
              </div>
            )}
          </div>
          {assessment.status === 'completed' && (
            <div className="score-display">
              Score: <span>{assessment.score}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* New Assessment Instructions Component */}
      {user && (
        <div className="assessment-instructions-container">
          <div className="assessment-header">
            <h2>
              {isUpdateMode ? 'Update Resilience Maturity Assessment' : 'Business Resilience Maturity Assessment'}
              {isUpdateMode && (
                <span className="update-mode-badge" style={{ 
                  marginLeft: '10px', 
                  fontSize: '14px', 
                  backgroundColor: '#D2BA7B', 
                  color: '#243444',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}>
                  Update Mode
                </span>
              )}
            </h2>
            <div>
              <button 
                onClick={() => saveAssessment()} 
                className="btn mr-2" 
                disabled={saving}
                style={isUpdateMode ? 
                  { backgroundColor: '#D2BA7B', color: '#243444' } : 
                  { backgroundColor: '#4C5454', color: '#FFFAFA' }
                }
              >
                {saving ? 'Saving...' : isUpdateMode ? 'Save Updates' : 'Save Draft'}
              </button>
              {!isUpdateMode && (
                <button 
                  onClick={() => saveAssessment('completed')} 
                  className="btn"
                  disabled={saving || responses.filter(r => r.answer && r.answer !== '').length === 0}
                  style={{ backgroundColor: '#243444', color: '#FFFAFA', fontWeight: 'bold' }}
                >
                  Submit Assessment
                </button>
              )}
            </div>
          </div>
          
          <div className="assessment-progress-bar-container">
            <div className="assessment-progress-label">Assessment Progress</div>
            <div className="assessment-progress-bar-wrapper">
              <div 
                className="assessment-progress-fill" 
                style={{ width: `${Math.max(1, (responses.filter(r => r.answer && r.answer !== '' && r.answer !== 'undefined').length / responses.length) * 100)}%` }}
              ></div>
            </div>
          </div>
          
          <div className="assessment-organization-header">
            <div className="assessment-org-info">
              <h3>Organization Resilience Assessment</h3>
              <p>{assessment.organizationName || user.organizationName || 'Your Organization'}</p>
            </div>
            <div className="assessment-meta-info">
              <p>Assessment Date: {new Date().toLocaleDateString()}</p>
              <p>Completed By: {user.name}</p>
            </div>
          </div>
          
          <div className="assessment-instructions-card" style={{ position: 'relative' }}>
            <h3>Assessment Instructions</h3>
            <p>This assessment evaluates your organization's resilience maturity across core essentials and functions. For each question:</p>
            <ol>
              <li>Read the question carefully</li>
              <li>Select the answer that best describes your organization's current state</li>
              <li>Add notes for context or justification (optional)</li>
            </ol>
            <p>Your responses will be used to calculate maturity scores and generate recommendations for improvement.</p>
          </div>
          
          <div className="assessment-progress-details">
            <div className="assessment-progress-count">
              {responses.filter(r => r.answer && r.answer !== '' && r.answer !== 'undefined').length} of {responses.length} questions answered ({Math.round(responses.filter(r => r.answer && r.answer !== '' && r.answer !== 'undefined').length / responses.length * 100)}%)
            </div>
            <div className="assessment-progress-bar">
              <div 
                className="assessment-progress-fill" 
                style={{ width: `${Math.max(1, (responses.filter(r => r.answer && r.answer !== '' && r.answer !== 'undefined').length / responses.length) * 100)}%` }}
              ></div>
            </div>
          </div>
          
          <div className="assessment-category-tabs">
            {categories.map(category => {
              const categoryQuestions = responses.filter(r => r.category === category);
              const answeredCount = categoryQuestions.filter(r => r.answer && r.answer !== '').length;
              const totalCount = categoryQuestions.length;
              
              return (
                <div key={category} className="category-tab-container" style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <button 
                    className={`assessment-category-tab ${activeCategory === category ? 'active' : ''} ${notApplicableCategories.includes(category) ? 'not-applicable' : ''}`}
                    onClick={() => setActiveCategory(category)}
                    style={notApplicableCategories.includes(category) ? { opacity: '0.7', textDecoration: 'line-through' } : {}}
                  >
                    {category}
                    <span className="badge">{answeredCount}/{totalCount}</span>
                  </button>
                  
                  <div className="not-applicable-checkbox" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    marginTop: '2px',
                    fontSize: '0.8rem'
                  }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: '#4C5454'
                    }}>
                      <input 
                        type="checkbox" 
                        checked={notApplicableCategories.includes(category)}
                        onChange={() => handleToggleNotApplicableCategory(category)}
                        style={{ marginRight: '4px' }}
                      />
                      Not Applicable
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {isUpdateMode && assessment.status === 'completed' && (
        <div className="alert alert-info">
          <strong>Update Mode:</strong> You can now review and update your previous answers to reflect your organization's current maturity level.
        </div>
      )}

      {assessment.status === 'completed' && !isUpdateMode ? (
        <div className="report-options">
          <h3>Generate reports based on your results:</h3>
          <div className="report-buttons">
            <button 
              onClick={() => generateReport('Scorecard')} 
              className="btn btn-primary"
              style={{ backgroundColor: '#243444', borderColor: '#243444', margin: '5px' }}
            >
              <FaChartBar /> Scorecard
            </button>
            <button 
              onClick={() => generateReport('ScorecardDetails')} 
              className="btn btn-primary"
              style={{ backgroundColor: '#243444', borderColor: '#243444', margin: '5px' }}
            >
              <FaFilePdf /> Scorecard Details
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="assessment-form">
            <form>
              {categoryQuestions.map((response, index) => (
                <div 
                  key={response.questionId} 
                  className={`assessment-question-item ${response.notApplicable ? 'not-applicable' : ''}`}
                  style={{ 
                    backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F5F5',
                    padding: '15px',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    opacity: response.notApplicable ? '0.7' : '1'
                  }}
                >
                  <div className="question-content" style={{ marginBottom: '0' }}>
                    {/* 1. Make the question font larger and add question number */}
                    <h3 style={{ 
                      fontSize: '1.15rem', 
                      fontWeight: 'bold',
                      color: '#243444',
                      margin: '0 0 5px 0',
                      lineHeight: '1.3',
                      display: 'flex'
                    }}>
                      <span style={{
                        backgroundColor: '#243444',
                        color: '#FFFAFA',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px',
                        fontSize: '0.9rem',
                        flexShrink: 0
                      }}>
                        {categoryQuestions.findIndex(r => r.questionId === response.questionId) + 1}
                      </span>
                      {response.question}
                    </h3>
                    
                    {/* 2. Move the helpful hint under the question */}
                    <p style={{ 
                      fontSize: '0.85rem',
                      color: '#4C5454',
                      marginTop: '0',
                      marginBottom: '8px',
                      fontStyle: 'italic',
                      lineHeight: '1.3'
                    }}>{response.description || generateHelpText(response.question)}</p>
                    
                    {/* 3. Move applicable standards to the left */}
                    <div className="question-standards-container" style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '10px'
                    }}>
                      <span className="standards-label" style={{ 
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        color: '#243444',
                        marginRight: '8px'
                      }}>Applicable standards:</span>
                      <div className="standards-badges" style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {/* Use either the response standards if available, or get standards based on question content */}
                        {(response.standards && typeof response.standards === 'string' && response.standards.trim() !== '') 
                          ? response.standards.split(',').map((standard, idx) => (
                              <span key={idx} className="standard-badge" style={{ 
                                backgroundColor: '#D2BA7B', 
                                color: '#243444',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                margin: '0 3px 3px 0',
                                display: 'inline-block',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                lineHeight: '1.2'
                              }}>{standard.trim()}</span>
                            ))
                          : getStandardsForQuestion(response.question).split(',').map((standard, idx) => (
                              <span key={idx} className="standard-badge" style={{ 
                                backgroundColor: '#D2BA7B', 
                                color: '#243444',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                margin: '0 3px 3px 0',
                                display: 'inline-block',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                lineHeight: '1.2'
                              }}>{standard.trim()}</span>
                            ))
                        }
                      </div>
                    </div>
                    
                    <div className="not-applicable-question-checkbox" style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      alignItems: 'center', 
                      marginBottom: '10px',
                      backgroundColor: '#f8f8f8',
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: '#4C5454',
                        margin: '0',
                        fontWeight: 'bold'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={response.notApplicable || false}
                          onChange={() => handleToggleNotApplicableQuestion(
                            responses.findIndex(r => r.questionId === response.questionId)
                          )}
                          style={{ marginRight: '8px' }}
                        />
                        Mark as Not Applicable to my organization
                      </label>
                      {response.notApplicable && (
                        <div style={{ 
                          marginLeft: '10px', 
                          fontSize: '0.85rem', 
                          fontStyle: 'italic',
                          color: '#7C1227'
                        }}>
                          This question will not affect your score
                        </div>
                      )}
                    </div>
                    
                    <div className="select-maturity-level" style={{ display: response.notApplicable ? 'none' : 'block' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label>Select the answer that best suits your organization:</label>
                        {savingQuestions[response.questionId] && (
                          <span style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            color: '#4C5454', 
                            fontSize: '0.85rem',
                            fontStyle: 'italic'
                          }}>
                            <FaSave style={{ marginRight: '5px' }} /> Saving...
                          </span>
                        )}
                        {savedAnswers[response.questionId] && !savingQuestions[response.questionId] && (
                          <span style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            color: '#4C5454', 
                            fontSize: '0.85rem'
                          }}>
                            <FaCheck style={{ marginRight: '5px', color: '#D2BA7B' }} /> Saved
                          </span>
                        )}
                      </div>
                      {response.maturityLevels && response.maturityLevels.length > 0 ? (
                        <select
                          className="assessment-maturity-select"
                          value={response.answer || ''}
                          onChange={(e) => {
                            const index = responses.findIndex(r => r.questionId === response.questionId);
                            if (index >= 0) {
                              handleAnswerChange(index, e.target.value);
                            }
                          }}
                        >
                          <option value=""></option>
                          {response.maturityLevels.map(level => (
                            <option key={level.level} value={level.level.toString()}>
                              {level.description}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          className="assessment-maturity-select"
                          value={response.answer || ''}
                          onChange={(e) => handleAnswerChange(
                            responses.findIndex(r => r.questionId === response.questionId),
                            e.target.value
                          )}
                        >
                          <option value=""></option>
                          <option value="0">No evidence or capability exists to ensure this practice.</option>
                          <option value="1">This practice occurs sporadically and informally without documentation or consistency.</option>
                          <option value="2">This practice is documented and repeatable in some areas but lacks enterprise-wide adoption.</option>
                          <option value="3">This practice is consistently performed, tracked, and resourced across most of the organization.</option>
                          <option value="4">This practice fully meets industry and regulatory standards and is regularly audited for effectiveness.</option>
                          <option value="5">This practice is continuously improved, benchmarked, and embedded in strategic decision-making.</option>
                        </select>
                      )}
                    </div>
                    
                    <div className="notes-section">
                      <label>Notes:</label>
                      <textarea 
                        className="assessment-notes-field"
                        placeholder="Enter any notes or comments about your selection..."
                        value={response.notes || ''}
                        onChange={(e) => handleNotesChange(
                          responses.findIndex(r => r.questionId === response.questionId),
                          e.target.value
                        )}
                      ></textarea>
                    </div>
                  </div>
                  
                  {/* Removed standalone saved indicator */}
                </div>
              ))}
              
              <div className="assessment-controls">
                {/* Category Navigation */}
                <div className="category-navigation">
                  <button
                    type="button"
                    onClick={() => {
                      const currentIndex = categories.indexOf(activeCategory);
                      if (currentIndex > 0) {
                        setActiveCategory(categories[currentIndex - 1]);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={categories.indexOf(activeCategory) === 0 || saving}
                    className="btn btn-light"
                  >
                    Previous Category
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const currentIndex = categories.indexOf(activeCategory);
                      if (currentIndex < categories.length - 1) {
                        setActiveCategory(categories[currentIndex + 1]);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={categories.indexOf(activeCategory) === categories.length - 1 || saving}
                    className="btn btn-light"
                  >
                    Next Category
                  </button>
                </div>
                
                {/* Save and Complete buttons */}
                <div className="action-buttons">
                  <button
                    type="button"
                    onClick={() => saveAssessment()}
                    disabled={saving}
                    className="btn btn-secondary"
                  >
                    <FaSave /> Save Progress
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => saveAssessment('completed')}
                    disabled={saving || responses.filter(r => r.answer && r.answer !== '').length === 0}
                    className="btn btn-primary"
                  >
                    <FaCheck /> Complete Assessment
                  </button>
                </div>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentDetail;
